import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { GAME_STAGES, DEFAULT_ROOM, HOME_PATH } from "../constants/gameStages";
import { useGameStage } from "../hooks/useGameStage";
import { SocketContext } from "../context/SocketProvider";
import { useRoleBackground } from "../hooks/useRoleBackground";
import { FaChevronRight, FaCircleQuestion } from "react-icons/fa6";
import AnimatedPointsBox from "../components/AnimatedPointsBox";
import PointAnimation from "../components/PointAnimation";
import { useLanguage } from "../context/LanguageContext";
import InfoPopup from "../components/InfoPopup";

function Points() {
  const { socket, role } = useContext(SocketContext);
  const { updateStage } = useGameStage();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [points, setPoints] = useState({ players: 0, ai: 0 });
  const [previousPoints, setPreviousPoints] = useState({ players: 0, ai: 0 });
  const [currentRound, setCurrentRound] = useState(1);
  const [infoPopupOpen, setInfoPopupOpen] = useState(false);
  const [voteStatus, setVoteStatus] = useState({
    votes: 0,
    required: 2,
    waiting_for: 2,
  });
  const [hasVoted, setHasVoted] = useState(false);
  const [newGameVoteStatus, setNewGameVoteStatus] = useState({
    votes: 0,
    required: 2,
    waiting_for: 2,
  });
  const [hasVotedNewGame, setHasVotedNewGame] = useState(false);
  const { bgStyle, className } = useRoleBackground("yellow");
  
  // Animation states for point changes
  const [lastAnimatedRoundData, setLastAnimatedRoundData] = useState(null);
  const [playerAnimationTrigger, setPlayerAnimationTrigger] = useState(0);
  const [aiAnimationTrigger, setAiAnimationTrigger] = useState(0);
  const [playerDiff, setPlayerDiff] = useState(0);
  const [aiDiff, setAiDiff] = useState(0);
  const [usedChallengeWords, setUsedChallengeWords] = useState([]);
  const [animationTimeouts, setAnimationTimeouts] = useState([]);

  useEffect(() => {
    if (!socket) return;

    // Listen for game data updates to get points and round
    const handleGameData = (data) => {
      console.log("Game data received:", data);
      
      if (data.points) {
        // Use previous points from backend data, fallback to current points if not provided
        const previousPointsData = data.previousPoints || points;
        
        console.log("Previous points:", previousPointsData);
        console.log("Current points:", data.points);
        
        // Always check for point differences
        const currentPlayerDiff = data.points.players - previousPointsData.players;
        const currentAiDiff = data.points.ai - previousPointsData.ai;
        
        // Only animate if there's an actual difference AND we haven't already animated this exact state
        const animationKey = `${previousPointsData.players}-${data.points.players}-${previousPointsData.ai}-${data.points.ai}`;
        const shouldAnimate = (currentPlayerDiff !== 0 || currentAiDiff !== 0) && animationKey !== lastAnimatedRoundData;
        
        // Check if this is just a sync update (no actual point change from our perspective)
        const isJustSync = (points.players === data.points.players && points.ai === data.points.ai);
        
        // Update states first
        setPreviousPoints(previousPointsData);
        setPoints(data.points);
        
        // Only trigger animations for actual changes that haven't been animated yet AND it's not just a sync
        if (shouldAnimate && !isJustSync) {
          setLastAnimatedRoundData(animationKey);
          console.log("🎬 Starting animations for key:", animationKey);
          
          // Clear any existing animation timeouts first
          animationTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
          setAnimationTimeouts([]);
          
          // Reset animation triggers to ensure clean state
          setPlayerAnimationTrigger(0);
          setAiAnimationTrigger(0);
          
          // Set the animation data and trigger
          setPlayerDiff(currentPlayerDiff);
          setAiDiff(currentAiDiff);
          setUsedChallengeWords(data.usedChallengeWords || []);
          
          // Wait for AnimatedPointsBox to finish its animation before showing popups
          // AnimatedPointsBox: 0.5 second delay + 1.0 second animation = 1.5 seconds total
          const newTimeouts = [];

          const animationTimeout = setTimeout(() => {
            if (currentPlayerDiff !== 0) {
              setPlayerAnimationTrigger(Date.now());
            }

            if (currentAiDiff !== 0) {
              setAiAnimationTrigger(Date.now());
            }
          }, 1600); // Wait for AnimatedPointsBox animation to complete (1.5s + 0.1s buffer)
          
          newTimeouts.push(animationTimeout);
          setAnimationTimeouts(newTimeouts);
        } else {
          console.log("🚫 Skipping animations - already shown, no change, or just sync update");
        }
      }
      if (data.round) {
        setCurrentRound(data.round);
      }
    };

    // Listen for vote status updates
    const handleNextRoundVote = (data) => {
      setVoteStatus(data);
    };

    // Listen for new game vote status updates
    const handleNewGameVote = (data) => {
      setNewGameVoteStatus(data);
    };

    // Listen for next round confirmation
    const handleNextRoundConfirmed = (data) => {
      // Clear any pending animations before navigation
      animationTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
      setAnimationTimeouts([]);
      setPlayerAnimationTrigger(0);
      setAiAnimationTrigger(0);

      setCurrentRound(data.round);
      setPoints(data.points);
      setHasVoted(false);
      setVoteStatus({ votes: 0, required: 2, waiting_for: 2 });
      setLastAnimatedRoundData(null); // Reset animation tracking

      // Use the new role from the server response if available, otherwise use context role
      const newRole = data.yourRole || role;
      const defaultMode = "normal";
      console.log("🎭 Next round navigation - role:", newRole);

      // From round 2 onwards, skip both game mode selection AND how to play, go directly to game
      console.log("🚀 Navigating directly to game (skipping intro for round 2+)");

      // Emit gamemode events for backend
      socket.emit("gamemode_selected", { mode: defaultMode, room: DEFAULT_ROOM });
      socket.emit("gamemode_confirmed", { mode: defaultMode, room: DEFAULT_ROOM });

      // Navigate directly to the game page based on role
      navigate(newRole === "describer" ? "/pickingimagenormal" : "/waitingfordescriber");
    };

    // Listen for new game confirmation 
    const handleNewGameConfirmed = (data) => {
      // Clear any pending animations before navigation
      animationTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
      setAnimationTimeouts([]);
      setPlayerAnimationTrigger(0);
      setAiAnimationTrigger(0);
      
      setHasVotedNewGame(false);
      setNewGameVoteStatus({ votes: 0, required: 2, waiting_for: 2 });
      setLastAnimatedRoundData(null); // Reset animation tracking
      updateStage(GAME_STAGES.READY);
      navigate(HOME_PATH);
    };

    // Listen for vote cancellations
    const handleNextRoundVoteCancelled = () => {
      console.log("🚫 Next round vote cancelled by a player");
      setHasVoted(false);
      setVoteStatus({ votes: 0, required: 2, waiting_for: 2 });
    };

    const handleNewGameVoteCancelled = () => {
      console.log("🚫 New game vote cancelled by a player");
      setHasVotedNewGame(false);
      setNewGameVoteStatus({ votes: 0, required: 2, waiting_for: 2 });
    };

    socket.on("game_data", handleGameData);
    socket.on("next_round_vote", handleNextRoundVote);
    socket.on("new_game_vote", handleNewGameVote);
    socket.on("next_round_confirmed", handleNextRoundConfirmed);
    socket.on("new_game_confirmed", handleNewGameConfirmed);
    socket.on("next_round_vote_cancelled", handleNextRoundVoteCancelled);
    socket.on("new_game_vote_cancelled", handleNewGameVoteCancelled);

    // Request current game data to get points and round
    socket.emit("get_game_data", { room: DEFAULT_ROOM });

    return () => {
      // Clear any pending animation timeouts
      animationTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
      
      socket.off("game_data", handleGameData);
      socket.off("next_round_vote", handleNextRoundVote);
      socket.off("new_game_vote", handleNewGameVote);
      socket.off("next_round_confirmed", handleNextRoundConfirmed);
      socket.off("new_game_confirmed", handleNewGameConfirmed);
      socket.off("next_round_vote_cancelled", handleNextRoundVoteCancelled);
      socket.off("new_game_vote_cancelled", handleNewGameVoteCancelled);
    };
  }, [socket, navigate, animationTimeouts]);

  const handleNextRound = () => {
    if (!hasVoted) {
      // If user had voted for new game, cancel that vote first
      if (hasVotedNewGame) {
        socket.emit("cancel_new_game_vote", { room: DEFAULT_ROOM });
        setHasVotedNewGame(false);
      }
      
      socket.emit("next_round", { room: DEFAULT_ROOM });
      setHasVoted(true);
    }
  };

  const handleTestPopup = () => {
    socket.emit("trigger_role_popup", {
      room: DEFAULT_ROOM,
      type: "next_round"
    });
  };

  const handleQuitPopup = () => {
    socket.emit("trigger_role_popup", {
      room: DEFAULT_ROOM,
      type: "quit"
    });
  };

  const handlePlayAgain = () => {
    if (!hasVotedNewGame) {
      // If user had voted for next round, cancel that vote first
      if (hasVoted) {
        socket.emit("cancel_next_round_vote", { room: DEFAULT_ROOM });
        setHasVoted(false);
      }
      
      socket.emit("reset_room", { 
        room: DEFAULT_ROOM,
        resetMode: true 
      });
      setHasVotedNewGame(true);
      handleQuitPopup();
    }
  };

  return (
    <div
      className={`p-4 md:p-8 lg:p-[64px] flex flex-col items-center min-h-screen ${className}`}
      style={bgStyle}
    >
      <div className="large-card flex flex-col min-h-[600px] relative">
        <h2 className="large-card-title bg-yellow400 py-[11px]">
          {t('score.title', 'SCORE TABLE')}
        </h2>

        {/* Info Button */}
        <button
          onClick={() => setInfoPopupOpen(true)}
          className="absolute top-1 right-4 text-white hover:scale-110 transition-transform z-20"
          title="Points Breakdown"
        >
          <FaCircleQuestion size={28} />
        </button>

        <div className="mt-[24px] mb-[4px]">
          <div className="flex justify-center gap-[80px] px-[96px] py-[24px] items-end relative h-[470px]">
            {/* Players Score */}
            <div className="relative">
              <AnimatedPointsBox
                previousPoints={previousPoints.players}
                currentPoints={points.players}
                title={t('score.players', 'PLAYERS')}
                bgColor="bg-blue-100"
                textColorTitle="text-pink400"
                textColorPoints="text-pink200"
              />
              
              {/* Player Point Animations */}
              <PointAnimation 
                totalDiff={playerDiff}
                usedChallengeWords={usedChallengeWords}
                isAI={false}
                position="left"
                trigger={playerAnimationTrigger}
              />
            </div>

            {/* AI Score */}
            <div className="relative">
              <AnimatedPointsBox
                previousPoints={previousPoints.ai}
                currentPoints={points.ai}
                title={t('score.aicom', 'AICOM-CHAN')}
                bgColor="bg-yellow-100"
                textColorTitle="text-yellow400"
                textColorPoints="text-yellow400"
              />
              
              {/* AI Point Animations */}
              <PointAnimation 
                totalDiff={aiDiff}
                usedChallengeWords={[]}
                isAI={true}
                position="right"
                trigger={aiAnimationTrigger}
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-row justify-end gap-[24px] px-[24px] mt-auto">
          <button
            className={`${
              hasVotedNewGame ? "btn-results-sm" : "btn-results-sm-transparent"
            } relative`}
            onClick={handlePlayAgain}
            disabled={hasVotedNewGame}
          >
            <span className="btn-text-pos-right-sm">
              {hasVotedNewGame ? t('button.waiting', 'Waiting') : t('button.newGame', 'New Game')}
            </span>
            <FaChevronRight className="arrow-pos-right-sm" style={{strokeWidth: '2.5'}} />
          </button>
          
          {currentRound < 4 && (
            <button
              className={`${
                hasVoted ? "btn-results-sm-transparent" : "btn-results-sm"
              } relative`}
              onClick={() => {
                handleNextRound();
                handleTestPopup();
              }}
              disabled={hasVoted}
            >
              <span className="btn-text-pos-right-sm">
                {hasVoted ? t('button.waiting', 'Waiting') : t('button.nextRound', 'Next Round')}
              </span>
              <FaChevronRight className="arrow-pos-right-sm" style={{strokeWidth: '2.5'}} />
            </button>
          )}
        </div>
      </div>

      <
      InfoPopup 
        isOpen={infoPopupOpen} 
        onClose={() => setInfoPopupOpen(false)} 
      />
    </div>
  );
}

export default Points;
